import React, { useState, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

interface CountryCode {
  code: string;
  name: string;
  dialCode: string;
  format: string;
  flag: string;
}

// Common country codes with formatting patterns
const COUNTRY_CODES: CountryCode[] = [
  { code: 'US', name: 'United States', dialCode: '+1', format: '(###) ###-####', flag: '🇺🇸' },
  { code: 'CA', name: 'Canada', dialCode: '+1', format: '(###) ###-####', flag: '🇨🇦' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', format: '#### ### ####', flag: '🇬🇧' },
  { code: 'AU', name: 'Australia', dialCode: '+61', format: '#### ### ###', flag: '🇦🇺' },
  { code: 'DE', name: 'Germany', dialCode: '+49', format: '#### ########', flag: '🇩🇪' },
  { code: 'FR', name: 'France', dialCode: '+33', format: '# ## ## ## ##', flag: '🇫🇷' },
  { code: 'ES', name: 'Spain', dialCode: '+34', format: '### ### ###', flag: '🇪🇸' },
  { code: 'IT', name: 'Italy', dialCode: '+39', format: '### ### ####', flag: '🇮🇹' },
  { code: 'JP', name: 'Japan', dialCode: '+81', format: '##-####-####', flag: '🇯🇵' },
  { code: 'CN', name: 'China', dialCode: '+86', format: '### #### ####', flag: '🇨🇳' },
  { code: 'IN', name: 'India', dialCode: '+91', format: '##### #####', flag: '🇮🇳' },
  { code: 'BR', name: 'Brazil', dialCode: '+55', format: '(##) #####-####', flag: '🇧🇷' },
  { code: 'MX', name: 'Mexico', dialCode: '+52', format: '## #### ####', flag: '🇲🇽' },
];

interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value = '',
  onChange,
  onValidationChange,
  label = 'Phone Number',
  placeholder,
  required = false,
  disabled = false,
  error,
  className = '',
}) => {
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(COUNTRY_CODES[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [localNumber, setLocalNumber] = useState('');
  const [isValid, setIsValid] = useState(true);

  // Parse existing value on mount
  useEffect(() => {
    if (value) {
      parsePhoneNumber(value);
    }
  }, []);

  const parsePhoneNumber = (phoneNumber: string) => {
    // Try to match with existing country codes
    for (const country of COUNTRY_CODES) {
      if (phoneNumber.startsWith(country.dialCode)) {
        setSelectedCountry(country);
        const localPart = phoneNumber.substring(country.dialCode.length).trim();
        setLocalNumber(formatLocalNumber(localPart, country.format));
        return;
      }
    }
    // If no match found, just set the local number
    setLocalNumber(phoneNumber);
  };

  const formatLocalNumber = (number: string, format: string): string => {
    // Remove all non-digits
    const digits = number.replace(/\D/g, '');
    
    // Apply formatting based on the country format
    let formatted = '';
    let digitIndex = 0;
    
    for (const char of format) {
      if (char === '#' && digitIndex < digits.length) {
        formatted += digits[digitIndex];
        digitIndex++;
      } else if (char !== '#') {
        formatted += char;
      }
    }
    
    return formatted;
  };

  const handleLocalNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const digits = inputValue.replace(/\D/g, '');

    // Cleared: emit '' (not a bare dial code and format like '+1 () -'), so the form can save the
    // clear. An empty optional phone is valid; a required one is not.
    if (digits.length === 0) {
      setLocalNumber('');
      setIsValid(!required);
      onValidationChange?.(!required);
      onChange?.('');
      return;
    }

    const formatted = formatLocalNumber(inputValue, selectedCountry.format);
    setLocalNumber(formatted);
    
    // Combine with country code
    const fullNumber = selectedCountry.dialCode + ' ' + formatted.trim();
    
    // Basic validation - check if we have enough digits
    const minDigits = selectedCountry.format.split('#').length - 1;
    const valid = digits.length >= Math.min(minDigits - 2, 7); // Allow some flexibility
    
    setIsValid(valid);
    onValidationChange?.(valid);
    onChange?.(fullNumber);
  };

  const handleCountrySelect = (country: CountryCode) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    
    // Reformat existing number with new country
    if (localNumber) {
      const formatted = formatLocalNumber(localNumber, country.format);
      setLocalNumber(formatted);
      const fullNumber = country.dialCode + ' ' + formatted.trim();
      onChange?.(fullNumber);
    }
  };

  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <div className="flex">
          {/* Country Code Dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
              disabled={disabled}
              aria-label="Select country code"
              aria-expanded={isDropdownOpen}
              aria-haspopup="listbox"
              className={`
                flex items-center px-3 py-2 min-h-11 border border-r-0 border-gray-300 bg-gray-50 text-sm
                rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                focus-visible:ring-2 focus-visible:ring-primary-500
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}
                ${error ? 'border-red-300' : ''}
              `}
            >
              <span className="mr-1">{selectedCountry.flag}</span>
              <span className="mr-1">{selectedCountry.dialCode}</span>
              <ChevronDownIcon className="w-4 h-4 text-gray-400" />
            </button>

            {isDropdownOpen && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsDropdownOpen(false)}
                />

                {/* Dropdown - Mobile-friendly positioning */}
                <div
                  className="absolute left-0 z-20 w-80 max-w-[calc(100vw-2rem)] max-h-60 overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg"
                  style={{ top: '100%', marginTop: '4px' }}
                  role="listbox"
                >
                  {COUNTRY_CODES.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => handleCountrySelect(country)}
                      role="option"
                      aria-selected={country.code === selectedCountry.code}
                      className="w-full px-3 py-3 min-h-11 text-left text-sm hover:bg-gray-100 flex items-center"
                    >
                      <span className="mr-2">{country.flag}</span>
                      <span className="mr-2">{country.dialCode}</span>
                      <span className="text-gray-600">{country.name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          
          {/* Phone Number Input - 44px minimum height for touch targets */}
          <input
            type="tel"
            value={localNumber}
            onChange={handleLocalNumberChange}
            placeholder={placeholder || selectedCountry.format.replace(/#/g, '0')}
            disabled={disabled}
            required={required}
            aria-label={label}
            aria-invalid={!isValid && localNumber ? 'true' : 'false'}
            className={`
              flex-1 px-3 py-2 min-h-11 border border-gray-300 rounded-r-md text-base
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
              focus-visible:ring-2 focus-visible:ring-primary-500
              disabled:opacity-50 disabled:cursor-not-allowed
              ${error ? 'border-red-300' : ''}
              ${!isValid && localNumber ? 'border-yellow-300 bg-yellow-50' : ''}
            `}
          />
        </div>
        
        {/* Validation feedback */}
        {!isValid && localNumber && (
          <p className="text-xs text-yellow-600 mt-1">
            Please enter a valid phone number
          </p>
        )}
        
        {error && (
          <p className="text-xs text-red-600 mt-1">{error}</p>
        )}
        
        {/* Format hint */}
        <p className="text-xs text-gray-500 mt-1">
          Format: {selectedCountry.dialCode} {selectedCountry.format.replace(/#/g, '0')}
        </p>
      </div>
    </div>
  );
};